import React, { useState, useRef } from 'react';
import { Form, Button, Row, Col } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import AuthLayout from './AuthLayout';
import dummyProfile from '../../assets/images/avatar.png';
import brandLogo from '../../assets/images/logo.png';

const SignUp = () => {
  const navigate = useNavigate();
  const [role, setRole] = useState('Student');
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    birthDate: '',
    language: '',
    country: '',
    timezone: '',
    description: ''
  });

  const [profileImage, setProfileImage] = useState(null);
  const [coverImage, setCoverImage] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});

  const profileInputRef = useRef(null);
  const coverInputRef = useRef(null);

  const languages = ['English', 'Spanish', 'French', 'German', 'Chinese', 'Japanese', 'Hindi', 'Arabic'];
  const countries = ['United States', 'United Kingdom', 'Canada', 'India', 'Australia', 'Germany', 'France', 'Japan'];
  const timezones = [
    '(GMT-08:00) Pacific Time',
    '(GMT-05:00) Eastern Time',
    '(GMT+00:00) UTC',
    '(GMT+01:00) Central European Time',
    '(GMT+05:30) India Standard Time',
    '(GMT+08:00) China Standard Time'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) setErrors({ ...errors, [name]: '' });
  };

  const handlePhoneChange = (value) => {
    setFormData({ ...formData, phone: value });
    if (errors.phone) setErrors({ ...errors, phone: '' });
  };

  const handleImageChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (type === 'profile') setProfileImage(reader.result);
        else setCoverImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Submitting Signup:', { ...formData, role, profileImage, coverImage });
    // After signup logic, redirect to login
    navigate('/login');
  };

  const isFormValid = formData.fullName &&
    formData.email &&
    formData.phone &&
    formData.password.length >= 8 &&
    formData.password === formData.confirmPassword;

  return (
    <AuthLayout
      showBrand={false}
      title={false}
      subtitle={false}
      wide={true}
    >
      <div className="wrapper text-center mt-n4 mb-3">
        <div className="profile-upload-section border border-3 border-primary rounded-circle position-relative shadow mx-auto"
          style={{ height: "100px", minWidth: "100px", width: "100px" }}
        >
          <Form.Control type="file" hidden ref={profileInputRef} onChange={(e) => handleImageChange(e, 'profile')} accept="image/*" required />
          <img src={profileImage || dummyProfile} className="profile-image-preview rounded-circle w-100 h-100" alt="Profile" />
          <div className="upload-btn bg-primary position-absolute bottom-0 end-0 pointer rounded-circle d-flex align-items-center justify-content-center"
            onClick={() => profileInputRef.current.click()}
            style={{ width: "26px", height: "26px" }}
          >
            <i className="bi bi-camera text-sm text-white"></i>
          </div>
        </div>
      </div>

      <div className="wrapper d-flex flex-wrap align-items-center gap-2 mb-3">
        <Link to="/" className="brand-logo text-primary d-flex align-items-center justify-content-center gap-2 text-decoration-none">
          <img src={brandLogo} alt="Logo" className="img-fluid" style={{ height: "40px" }} />
        </Link>
        <div className="info-wrapper">
          <div className="title text-base text-gray-900 fw-bold">Create your account</div>
          <div className="subtitle text-sm text-gray-500">Complete your profile to get started</div>
        </div>
      </div>

      <div className="role-selector d-flex gap-3 mb-3">
        <div className={`role-option ${role === 'Student' ? 'active' : ''}`} onClick={() => setRole('Student')}>Join as Student</div>
        <div className={`role-option ${role === 'Teacher' ? 'active' : ''}`} onClick={() => setRole('Teacher')}>Join as Teacher</div>
      </div>

      <Form onSubmit={handleSubmit}>
        <Row className="g-3 mb-3">
          <Col sm={6}>
            <Form.Group>
              <Form.Label>Full Name</Form.Label>
              <Form.Control type="text" name="fullName" placeholder="Enter your name" value={formData.fullName} onChange={handleInputChange} required />
            </Form.Group>
          </Col>
          <Col sm={6}>
            <Form.Group>
              <Form.Label>Email Address</Form.Label>
              <Form.Control type="email" name="email" placeholder="Enter your email" value={formData.email} onChange={handleInputChange} required />
            </Form.Group>
          </Col>
        </Row>

        <Row className="g-3 mb-3">
          <Col sm={6}>
            <Form.Group>
              <Form.Label>Mobile Number</Form.Label>
              <PhoneInput
                country={'in'}
                value={formData.phone}
                onChange={handlePhoneChange}
                placeholder="Enter mobile number"
                inputProps={{
                  required: true,
                }}
              />
            </Form.Group>
          </Col>
          <Col sm={6}>
            <Form.Group>
              <Form.Label>Date of Birth</Form.Label>
              <Form.Control type="date" name="birthDate" value={formData.birthDate} onChange={handleInputChange} required />
            </Form.Group>
          </Col>
        </Row>

        <Row className="g-3 mb-3">
          {role === 'Teacher' && (
            <Col sm={6}>
              <Form.Group>
                <Form.Label>Your Current Language</Form.Label>
                <Form.Select name="language" value={formData.language} onChange={handleInputChange} required >
                  <option value="" hidden>Select Language</option>
                  {languages.map(lang => <option key={lang} value={lang}>{lang}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>
          )}
          <Col sm={6}>
            <Form.Group>
              <Form.Label>Select Country</Form.Label>
              <Form.Select name="country" value={formData.country} onChange={handleInputChange} required >
                <option value="" hidden>Select Country</option>
                {countries.map(c => <option key={c} value={c}>{c}</option>)}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col sm={role === 'Teacher' ? 12 : 6}>
            <Form.Group>
              <Form.Label>Select Timezone</Form.Label>
              <Form.Select name="timezone" value={formData.timezone} onChange={handleInputChange} required >
                <option value="" hidden>Select Timezone</option>
                {timezones.map(tz => <option key={tz} value={tz}>{tz}</option>)}
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        <Row className="g-3 mb-3">
          <Col sm={6}>
            <Form.Group>
              <Form.Label>Create Password</Form.Label>
              <div className="password-icon-wrapper">
                <Form.Control
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  placeholder="Enter password"
                  value={formData.password}
                  onChange={handleInputChange}
                  maxLength={128}
                  required
                />
                <span className="eye-btn"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <i className="bi bi-eye-slash"></i> : <i className="bi bi-eye"></i>}
                </span>
              </div>
            </Form.Group>
          </Col>
          <Col sm={6}>
            <Form.Group>
              <Form.Label>Confirm Password</Form.Label>
              <div className="password-icon-wrapper">
                <Form.Control
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  placeholder="Enter confirm password"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  maxLength={128}
                  required
                />
                <span className="eye-btn"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <i className="bi bi-eye-slash"></i> : <i className="bi bi-eye"></i>}
                </span>
              </div>
            </Form.Group>
          </Col>
        </Row>

        <div className="cover-upload-section mb-3">
          <Form.Label>Upload Profile Cover</Form.Label>
          <Form.Control type="file" hidden ref={coverInputRef} onChange={(e) => handleImageChange(e, 'cover')} accept="image/*" required />
          <div className="cover-image-preview d-flex align-items-center justify-content-center overflow-hidden position-relative pointer rounded" onClick={() => coverInputRef.current.click()} >
            {coverImage ? (
              <img src={coverImage} alt="Cover" className="h-100 w-100 object-fit-cover" />
            ) : (
              <div className="text-center text-muted">
                <div className="icon mb-1"><i className="bi bi-plus-circle-dotted text-xl"></i></div>
                <div className="small fw-medium">Add cover banner</div>
              </div>
            )}
            {coverImage && <div className="upload-overlay">Change Banner</div>}
          </div>
        </div>

        <Form.Group className="mb-4">
          <Form.Label>Bio Information</Form.Label>
          <Form.Control as="textarea" name="description" rows={3} placeholder="Tell us a little about yourself ..." value={formData.description} onChange={handleInputChange} />
        </Form.Group>

        <Button variant="primary" type="submit" className="w-100 btn-custom" disabled={!isFormValid}>
          Create {role} Account
        </Button>

        <div className="auth-footer">
          Already have an account? <Link to="/login">Sign In</Link>
        </div>
      </Form>
    </AuthLayout>
  );
};

export default SignUp;
